import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';

import { AccountSelectors } from '@core/account-data';
import { DateRangeValueType } from '@core/date-range-types';
import { I18nService } from '@core/i18n-legacy';
import {
  ReportDashboardMetricOptions,
  ReportDateFilter,
  AvailableDateField,
  ReportsComponentTileFilterBreadcrumbSync,
  ReportsFilterBreadcrumb,
  BreadcrumbsService,
  BreadcrumbsServiceSymbol,
  BreadcrumbsParams,
  AbsoluteDatesData,
  BreadcrumbsData,
} from '@core/reports-types';
import { getAvailableDateFields } from '@core/reports-util';
import { getDateRangePickerLabel } from '@core/time-ui';

@Injectable()
export class DateBreadcrumbsService implements BreadcrumbsService<'date'> {
  public [BreadcrumbsServiceSymbol] = <const>'date';
  private i18n = inject(I18nService);
  private store = inject(Store);

  public getBreadcrumbs(params: BreadcrumbsParams & AbsoluteDatesData): ReportsFilterBreadcrumb[] {
    return this.getBreadcrumbsSync(params);
  }
  public getBreadcrumbsSync(
    params: BreadcrumbsParams & AbsoluteDatesData,
  ): ReportsComponentTileFilterBreadcrumbSync[] {
    const { data, componentSavedFilters, metric } = params;

    if (data?.options?.showAllData) {
      return this.allData(params);
    }
    const translatedOriginalDate = !componentSavedFilters
      ? null
      : this.getDateFilterTranslated(data, metric, componentSavedFilters.dateFilter, params);

    const translatedAppliedDate = this.getDateFilterTranslated(
      data,
      metric,
      data.dateFilter,
      params,
    );

    if (!translatedAppliedDate) {
      return [];
    }
    return [
      {
        simple: false,
        invalid: false,
        translatedGroup: translatedAppliedDate.group,
        translatedValues: [{ label: translatedAppliedDate.value }],
        hasOverwrites: !deepEqual(translatedOriginalDate, translatedAppliedDate),
      },
    ];
  }

  private allData({
    data,
    componentSavedFilters,
    absoluteDates,
  }: BreadcrumbsParams & AbsoluteDatesData): ReportsComponentTileFilterBreadcrumbSync[] {
    const globalDateFilterApplied = !deepEqual(data.dateFilter, componentSavedFilters?.dateFilter);

    return [
      {
        simple: true,
        bold: false,
        icon:
          globalDateFilterApplied && !absoluteDates
            ? {
                name: 'warning',
                color: 'colorIconWarningDefault',
              }
            : undefined,
        tooltipText:
          globalDateFilterApplied && !absoluteDates
            ? this.i18n.translate('tlr.filters_tooltip.all_data_with_global_date_info')
            : undefined,
        translatedValue: this.i18n.translate('tlr.component_builder_dialog.filter_all_data_label'),
        hasOverwrites: false,
      },
    ];
  }

  private getDateFilterTranslated(
    data: BreadcrumbsData,
    metric: ReportDashboardMetricOptions,
    dateFilter: ReportDateFilter,
    absoluteDatesData: AbsoluteDatesData,
  ): { group: string; value: string } | null {
    const availableDateFields = getAvailableDateFields(data, metric);
    const dateField = availableDateFields.find(
      f => f.field === data.dateField?.field && f.resource === data.dateField?.resource,
    )!;

    if (!dateField) {
      return null;
    }

    let value: string;
    if (absoluteDatesData.absoluteDates === true) {
      value = this.getAbsoluteDatesValue(data, absoluteDatesData, dateField);
    } else {
      value = getDateRangePickerLabel(
        {
          type: <
            DateRangeValueType // To display full date, Custom type is needed
          >(dateFilter.dateRange !== DateRangeValueType.Range ? dateFilter.dateRange : DateRangeValueType.Custom),
          min: dateFilter.dateStart,
          max: dateFilter.dateEnd,
        },
        this.i18n,
      );
    }

    const { name: group } = dateField;
    return {
      group,
      value,
    };
  }

  private getAbsoluteDatesValue(
    data: BreadcrumbsData,
    absoluteDatesData: AbsoluteDatesData & { absoluteDates: true },
    dateField: AvailableDateField,
  ): string {
    if (data.dateFilter.dateRange === DateRangeValueType.AllTime) {
      return getDateRangePickerLabel(
        {
          type: DateRangeValueType.AllTime,
        },
        this.i18n,
      );
    }
    const company = this.store.selectSignal(AccountSelectors.currentCompany)();
    const dateEnd =
      // single exception for candidate_start_date
      dateField.id === 'candidate_start_date'
        ? new Date(absoluteDatesData.meta.dateEnd!)
        : Math.min(new Date(absoluteDatesData.meta.dateEnd!).getTime(), new Date().getTime());
    const max = new Date(dateEnd).toISOString();
    const startDate = Math.max(
      new Date(absoluteDatesData.meta.dateStart!).getTime(),
      company?.createdAt ? new Date(company.createdAt).getTime() : 0,
    );
    const min = new Date(startDate).toISOString();
    return getDateRangePickerLabel(
      {
        type: DateRangeValueType.Custom,
        min,
        max,
      },
      this.i18n,
    );
  }
}
